import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {AnalyticsService} from "../orchestration/analytics.service";
import {SessionModel} from "../orchestration/session.model";
import {NavigationService} from "../orchestration/navigation.service";
import {SessionController} from "../orchestration/session.controller";
import {AuthService} from "@fedelta-pos/data-client";
import {NotificationService} from "../notification/notification.service";
import {PageMode, ResponsiveService} from "../orchestration/responsive.service";
import {BehaviorSubject, Observable, Subject, Subscription} from "rxjs";
import {IdentityModel} from "../identity/identity.model";
import {LoadingStatus} from "../common-types/loading-status";
import {LoadingService} from "../ui-elements/loading/loading.service";
import {
    DesktopHomePageLayoutComponent
} from "../page-layout/desktop-home-page-layout/desktop-home-page-layout.component";
import {ButtonProminence} from "../buttons/button/button";
import {ReorderStatus} from "./reorder-status";
import {MessageLevel} from "../notification/message/message";
import {IconButtonType} from "../buttons/icon-button/icon-button";
import {IdentificationMode} from "../identity/identification-page/identification-mode";
import {GroupOrderPageMode} from "../group-order/group-order-page/group-order-page-mode";
import {PromoModel} from "../promo/promo.model";
import {GroupOrderModel} from "../group-order/group-order.model";
import {MenuModel} from "../menu/menu.model";
import {DealModel} from "../deal/deal.model";
import {OrderModel} from "../order/order.model";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {OrderingContextController} from "../ordering-context/ordering-context.controller";
import {OrchestrationEvents} from "../orchestration/orchestration-events";
import {OrderingContextEvents} from "../ordering-context/ordering-context-events";
import {IdentityEvents} from "../identity/identity-events";
import {MenuCategorySelection, MenuEvents, MenuItemSelection} from "../menu/menu-events";
import {GroupOrderEvents} from "../group-order/group-order-events";
import {DealEvents} from "../deal/deal-events";
import {OrderEvents, OrderInitiationStatus, OrderSubmissionStatus} from "../order/order-events";
import {OrderingContext, OrderingContextIntention} from "../ordering-context/ordering-context";
import {FullPageLayoutComponent} from "../page-layout/full-page-layout/full-page-layout.component";
import {StringUtils} from "../string-utils/string-utils";
import {Page, PageAlignment} from "../orchestration/page";
import {OrderingPhase, SessionEvents} from "../orchestration/session-events";
import {AppPromoAction, AppPromoData} from "../identity/member-summary-entity.model";
import {ActiveSchedulesSearchModel} from "../schedule/active-schedules-search.model";
import {RecentOrderData} from "../order/recent-order.service";
import {
    EntitlementData,
    EntitlementProductData,
    EntitlementResult,
    EntitlementResultCode,
    EntitlementStatus
} from "../deal/entitlement-entity.model";
import {IntegratedPaymentProvider, PaymentType} from "../payment/payment";
import {OrderItem, OrderType} from "../order/order";
import {MenuItemData} from "../menu/menu-entity.model";
import {PaymentModel} from "../payment/payment.model";
import {PaymentEvents} from "../payment/payment-events";

class ReloadRequest {
    public requestCount: number = 1;
    public deferred: boolean = false;

    constructor(public forceLoadApp: boolean = false,
                public forceLoadMenu: boolean = false,
                public forceInitiate: boolean = false,
                public forceLoadStores: boolean = true) {
    }

    update(forceLoadApp: boolean, forceLoadMenu: boolean, forceInitiate: boolean, forceLoadStores: boolean): ReloadRequest {
        this.forceLoadApp = this.forceLoadApp || forceLoadApp;
        this.forceLoadMenu = this.forceLoadMenu || forceLoadMenu;
        this.forceInitiate = this.forceInitiate || forceInitiate;
        this.forceLoadStores = this.forceLoadStores || forceLoadStores;
        this.requestCount++;
        return this;
    }
}

@Component({
    selector: "woa-ordering-page",
    templateUrl: "./ordering-page.component.html",
    providers: [{provide: Page, useExisting: OrderingPageComponent}],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderingPageComponent extends Page implements OnInit, OnDestroy {
    PageAlignment = PageAlignment;
    MessageLevel = MessageLevel;
    IconButtonType = IconButtonType;
    OrderInitiationStatus = OrderInitiationStatus;
    LoadingStatus = LoadingStatus;
    OrchestrationEvents = OrchestrationEvents;
    OrderEvents = OrderEvents;
    StringUtils = StringUtils;

    constructor(private orderingContextController: OrderingContextController,
                private notificationService: NotificationService,
                private analyticsService: AnalyticsService,
                private loadingService: LoadingService,
                private changeDetectorRef: ChangeDetectorRef,
                private element: ElementRef,
                private authService: AuthService,
                private dealModel: DealModel,
                private paymentModel: PaymentModel,
                private identityModel: IdentityModel,
                private activeSchedulesSearchModel: ActiveSchedulesSearchModel,
                public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                public orderModel: OrderModel,
                public menuModel: MenuModel,
                public promoModel: PromoModel,
                public groupOrderModel: GroupOrderModel,
                public navigationService: NavigationService,
                public sessionController: SessionController,
                public responsiveService: ResponsiveService) {
        super();
    }

    /* ================
        PAGE LIFECYCLE
       ================ */

    private subscriptions: Subscription = new Subscription();

    ngOnInit(): void {
        this.subscriptions.add(this.responsiveService.mode$.subscribe(() => {
            if (this.responsiveService.mobile && !this.viewingMenuItems$.value && this.menuModel.selectedCategory && !this.menuModel.selectedCategory.id) {
                this.menuModel.deactivatePromotedCategory();
            }
            this.changeDetectorRef.detectChanges();
            this.refreshScrolling();
        }));
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(this.responsiveService.windowHeight$.subscribe(() => this.refreshScrolling()));

        this.subscriptions.add(this.activeSchedulesSearchModel.$data.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(this.notificationService.alertDisplayed$.subscribe(() => this.changeDetectorRef.detectChanges()));

        this.subscriptions.add(SessionEvents.orderingPhase$.subscribe((orderingPhase: OrderingPhase) => {
            if (orderingPhase === OrderingPhase.ORDERING) {
                this.requestReload("returned to ordering", false, false, true, true);
            }
        }));

        this.subscriptions.add(IdentityEvents.loginRevoked$.subscribe(() => this.requestReload("login revoked", true, true, false, true)));
        this.subscriptions.add(IdentityEvents.loginComplete$.subscribe(() => this.requestReload("login complete", true, true, false, true)));

        this.subscriptions.add(OrderingContextEvents.storeDetailsChanged$.subscribe(() => this.requestReload("store details changed", true, true, false, false)));
        this.subscriptions.add(OrderingContextEvents.orderType$.subscribe(() => this.requestReload("order type changed", false, true, false, false)));
        this.subscriptions.add(OrderingContextEvents.evaluatingExpectedCompletionChange$.subscribe((evaluating: boolean) => {
            if (!evaluating) {
                this.orderModel.requestOrderInitiation("ordering page - time change");
                this.changeDetectorRef.detectChanges();
            }
        }));

        this.subscriptions.add(OrderEvents.requestOrderItem$.subscribe(async (menuItemSelection: MenuItemSelection) => this.orderItem(menuItemSelection, false, false, false)));
        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => {
            if (!this.orderModel.hasOrderedItems()) {
                this.viewingOrder$.next(false);
            }
        }));
        this.subscriptions.add(OrderEvents.orderInitiationStatus$.subscribe((status: OrderInitiationStatus) => this.orderInitiationStatusChanged(status)));
        this.subscriptions.add(OrderEvents.orderInitiationRequestReceived$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(PaymentEvents.isSubmittingWalletPayment$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(OrderEvents.orderSubmissionStatus$.subscribe(async (status: OrderSubmissionStatus) => {
            if (status === OrderSubmissionStatus.ORDER_INVALID) {
                this.requestReload("order invalid on submission", false, true, false, false);
                return;
            }
            if (status === OrderSubmissionStatus.ORDER_CANCELLED) {
                return this.orderModel.completeOrder();
            }
            this.changeDetectorRef.detectChanges();
        }));

        this.subscriptions.add(MenuEvents.currentMenu.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(MenuEvents.menuBuilt$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(MenuEvents.categorySelected$.subscribe((selection: MenuCategorySelection) => {
            if (!selection.category) {
                this.viewingMenuItems$.next(false);
            } else if (this.responsiveService.mobile && (this.viewingMenuItems$.value || !selection.reselected)) {
                // noinspection JSIgnoredPromiseFromCall
                this.viewCategory();
            }
        }));

        this.subscriptions.add(GroupOrderEvents.groupOrderStatus$.subscribe(() => this.changeDetectorRef.detectChanges()));

        this.subscriptions.add(DealEvents.currentEntitlementStatus$.subscribe((status: EntitlementStatus) => this.orderDealProducts(status)));

        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => this.refreshScrolling()));

        this.requestReload("init", false, false, false, true);
        this.checkReorder();
    }

    onActive(): void {
        this.sessionModel.orderingPhase = OrderingPhase.ORDERING;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.reloadSubscription && this.reloadSubscription.unsubscribe();
        this.menuModel.unloadMenu();
        this.stopReorderTimer();
    }

    /* ========================
        LOAD/RELOAD MANAGEMENT
       ======================== */

    public loadingStatus: LoadingStatus = undefined;

    private initialLoadStatusSubscribed: boolean = false;
    private reloadRequested$: Subject<void> = new Subject<void>();
    private reloadDebouncer: Observable<void> = this.reloadRequested$.debounceTime(500);
    private reloadSubscription: Subscription;
    private latestReloadRequest: ReloadRequest;

    public get isLoaded(): boolean {
        return this.loadingStatus === LoadingStatus.LOADED || this.loadingStatus === LoadingStatus.RELOADING;
    }

    /**
     * Reload requests are debounced because multiple events can require a reload,
     * and these events may be triggered by a single action (such as store and order type selection).
     * Debouncing allows the reload logic to be decoupled from the event triggers, while avoiding redundant lookups and race conditions.
     * @param reason
     * @param forceLoadApp
     * @param forceLoadMenu
     * @param forceInitiate
     * @param forceLoadStores
     */
    public requestReload(reason: string, forceLoadApp: boolean, forceLoadMenu: boolean, forceInitiate: boolean, forceLoadStores: boolean): void {
        console.log(Date.now(), "Ordering page reload requested:", forceLoadApp, forceInitiate, forceLoadStores, reason);

        // Record the reload request. Subsequent requests during a debounce period are merged.
        this.latestReloadRequest = this.latestReloadRequest ? this.latestReloadRequest.update(forceLoadApp, forceLoadMenu, forceInitiate, forceLoadStores) : new ReloadRequest(forceLoadApp, forceLoadMenu, forceInitiate, forceLoadStores);

        // Subscribe to reload requests on the first request
        if (!this.reloadSubscription) {
            this.reloadSubscription = this.reloadDebouncer.subscribe(async (): Promise<void> => {

                // Defer concurrent reload attempts by re-requesting.
                // As requests are debounced, any further reloads triggered by actions DURING reload will effectively be batched.
                if (this.loadingStatus === LoadingStatus.LOADING || this.loadingStatus === LoadingStatus.RELOADING) {
                    console.log(Date.now(), "Deferring concurrent ordering page reload");
                    this.latestReloadRequest.deferred = true;
                    setTimeout(() => this.reloadRequested$.next(), 500);
                    return;
                }

                // Perform the merged reload request
                const request: ReloadRequest = this.latestReloadRequest;
                this.latestReloadRequest = undefined;
                return this.reload(request);
            });
        }

        // Queue the request to the debouncer
        this.reloadRequested$.next();
    }

    private async reload(request: ReloadRequest): Promise<void> {
        if (SessionEvents.orderingPhase$.value !== OrderingPhase.ORDERING) {
            console.log(Date.now(), "Not performing ordering page reload for " + request.requestCount + " merged request(s) - not in ordering phase");
            return;
        }

        console.log(Date.now(), "Performing " + (request.deferred ? "deferred " : "") + "ordering page reload for " + request.requestCount + " merged request(s)");

        let menuLoadRequired: boolean = request.forceLoadMenu || !this.menuModel.menuLoaded || this.orderModel.orderTypeChangedSinceLastInitiation;
        let canReorder: boolean = false;

        try {
            this.loadingStatus = this.loadingStatus === LoadingStatus.LOADED ? LoadingStatus.RELOADING : LoadingStatus.LOADING;
            this.changeDetectorRef.detectChanges();

            await this.loadingService.performWithSpinner(async () => {
                await this.sessionModel.loadApp(request.forceLoadApp);

                // These can be done in parallel
                await Promise.all([
                    this.orderingContextModel.loadStores(request.forceLoadApp && request.forceLoadStores),
                    this.orderModel.verifyPendingOrders()
                ]);

                // Member summary and customer payment details require stores to have been loaded as this may select the preselected store.
                await Promise.all([
                    this.identityModel.loadMemberSummary(),
                    this.paymentModel.loadCustomerPaymentDetails("ordering page - reload")
                ]);

                // Menu must be loaded after app and stores.
                // Don't load the menu if there are subsequent reload requests pending.
                if (menuLoadRequired) {
                    if (this.latestReloadRequest) {
                        menuLoadRequired = false;
                        this.latestReloadRequest.forceLoadMenu = true;
                    } else {
                        await this.menuModel.loadMenu("ordering page - reload");
                    }
                }

                this.filterPromotionalCards();

                // Don't initiate the order if there are subsequent reload requests pending.
                if (request.forceInitiate || menuLoadRequired) {
                    if (this.latestReloadRequest) {
                        this.latestReloadRequest.forceInitiate = true;
                    } else {
                        this.orderModel.requestOrderInitiation("ordering page - reload", true);
                    }
                }

                // Note - canRestoreOnLoad and canReorderOnLoad must be checked before the potential return cases which follow,
                // because these are single-shot methods which only return true once until reset.
                // If the return cases occur, we don't want to try the onLoad cases again.
                const canRestore: boolean = this.orderModel.canRestoreOnLoad;
                canReorder = this.orderModel.canReorderOnLoad;

                if (await this.orderPreselectedDealCode()) {
                    return;
                }

                if (canRestore) {
                    if (await this.restoreOrder()) {
                        // We have restored order items, so reorder is no longer an option
                        return;
                    }
                    if (!canReorder) {
                        // We may have restored just the store and order type, so we can check again whether reorder is an option
                        canReorder = this.orderModel.canReorderOnLoad;
                    }
                }
            }, menuLoadRequired ? 10000 : 0, "woa.message.order.loading");

            this.loadingStatus = LoadingStatus.LOADED;
        } catch (error) {
            // Don't report an error if there is no login (including a guest login).
            // This can happen if the user login is revoked on the back-end. It should resolve and refresh once the guest login is re-established.
            console.log(error);
            if (this.authService.isLoggedIn()) {
                this.loadingStatus = LoadingStatus.FAILED;
            } else {
                console.log("Ignoring reload error as there is no login");
            }
        } finally {
            this.refreshScrolling();
            this.sessionModel.notifyInitialLoadStatus(LoadingStatus.LOADED);
            this.changeDetectorRef.detectChanges();

            // Display of prompts is deferred until after loading is complete
            await this.checkOnLoadPrompts("Reload", canReorder);
        }
    }

    /**
     * Called on completion of page load and order initiation.
     * Prompts as necessary for verified orders, group order participation, and reorder round.
     * @param reason
     * @param checkReorder
     */
    private async checkOnLoadPrompts(reason: string, checkReorder: boolean): Promise<void> {
        console.log("Checking on-load prompts:", reason, checkReorder);
        if (OrchestrationEvents.initialLoadStatus$.value === LoadingStatus.LOADING) {
            if (!this.initialLoadStatusSubscribed) {
                this.initialLoadStatusSubscribed = true;
                this.subscriptions.add(OrchestrationEvents.initialLoadStatus$.distinct().subscribe(() => this.checkOnLoadPrompts("Initial load complete", checkReorder)));
            }
            return;
        }

        if (this.loadingStatus !== LoadingStatus.LOADED || SessionEvents.orderingPhase$.value !== OrderingPhase.ORDERING || !this.navigationService.pageIsActive(this)) {
            return;
        }

        await this.orderModel.notifyVerifiedOrders();

        await this.groupOrderModel.promptForGroupOrderParticipation();

        if (checkReorder) {
            if (this.orderModel.canPromptForReorder) {
                return this.openReorderPage();
            }
            this.orderModel.canReorderOnLoad = true;
        }
    }

    /* ============
        PAGE STATE
       ============ */

    public get showMobileNavMenuIcon(): boolean {
        return !this.viewingOrder$.value && !this.viewingMenuItems$.value && !this.sessionModel.waiterMode;
    }

    public get showOrderingContextPanel(): boolean {
        return this.responsiveService.desktop || this.viewingOrder$.value || !this.viewingMenuItems$.value;
    }

    public get showFeatureButtons(): boolean {
        return this.responsiveService.desktop || this.viewingOrder$.value || !this.viewingMenuItems$.value;
    }

    public get showMobileFooter(): boolean {
        return !this.viewingOrder$.value ||
            (this.loadingStatus === LoadingStatus.LOADED &&
                OrderEvents.orderInitiationStatus$.value !== OrderInitiationStatus.FAILED &&
                (this.sessionModel.loginOptions.allowGuestOrdering || this.identityModel.allowMemberLogin));
    }

    public get viewingMobileView(): boolean {
        return this.responsiveService.mobile && (this.viewingOrder$.value || this.viewingMenuItems$.value);
    }

    /* ===================
        CAROUSEL CONTENTS
       =================== */

    private static readonly MAX_PROMOTED_DEALS: number = 5;
    private static readonly MAX_PROMOTED_CARD_PROMOS: number = 5;

    // Based on a minimum slide height of 162px, 15px gap and margins, and a slide aspect ratio of 16:9
    private static readonly MIN_SCREEN_WIDTH_FOR_TWO_SLIDES: number = 621;

    public promotedDeals: EntitlementData[] = [];
    public promotedCardPromos: AppPromoData[] = [];
    public triggeredAndUnsuppressedCardPromos: AppPromoData[] = [];
    public morePromotionalCards: boolean;

    private allAvailableDeals: EntitlementData[] = [];
    private allUntriggeredCardPromos: AppPromoData[] = [];

    public get showCarousel(): boolean {
        if (this.viewingMobileView || !this.isLoaded || this.sessionModel.waiterMode) {
            return false;
        }
        if (this.identityModel.promoteMemberLogin) {
            return true;
        }
        if ((this.responsiveService.desktop && !this.sessionModel.currentApp.showDesktopMenuPromos) || (this.responsiveService.mobile && !this.sessionModel.currentApp.showMobileMenuPromos)) {
            return false;
        }
        if (this.promotedDeals.length > 0) {
            return true;
        }
        return this.orderModel.personalOrderStarted ? this.triggeredAndUnsuppressedCardPromos.length > 0 : this.promotedCardPromos.length > 0;
    }

    public get showMobileHeaderImage(): boolean {
        return !this.viewingOrder$.value && !this.viewingMenuItems$.value && !this.showCarousel && !!this.orderingContextModel.headerImage;
    }

    public get nrVisibleSlides(): number {
        const mode: PageMode = this.responsiveService.calculateMode(this.responsiveService.calculateAvailableWidth(this.element.nativeElement, this.desktopHomePage ? "desktop" : undefined));
        return mode == PageMode.WIDEST_DESKTOP ? 6
            : mode == PageMode.WIDER_DESKTOP ? 5
                : mode == PageMode.WIDE_DESKTOP ? 4
                    : mode == PageMode.BASIC_DESKTOP ? 3
                        : mode == PageMode.NARROW_DESKTOP ? 2
                            : mode == PageMode.MOBILE && window.innerWidth >= OrderingPageComponent.MIN_SCREEN_WIDTH_FOR_TWO_SLIDES ? 2
                                : 1;
    }

    /**
     * Finds the list of card deals and promos suppressed or triggered by the currently ordered items.
     *
     * Rules for picking triggered card promos:
     * 1. The promo must be triggered and configured to "showAppPromoCard";
     * 2. The promo must be triggered by a sellable which has been ordered;
     * 3. If the promo has promoted sellables:
     *      a) One or more promoted sellables must be both:
     *          i)  not on the order; and
     *          ii) on the menu.
     *
     * Additional filtering will be done in the promo-card component to filter out promoted sellables which are already on the order.
     *
     * Previously, only explicitly triggered card promos were shown after items were ordered (as detailed above).
     * Now, individual promos may be configured to NOT be suppressed after ordering products,
     * or optionally only suppressed if a promoted product has been ordered (as for the popups option).
     * These unsuppressed promos are included in the list (taken from the promotedCardPromos array).
     */
    private filterPromotionalCards(): void {
        if (!this.identityModel.currentMember) {
            return;
        }
        this.allAvailableDeals = this.identityModel.currentMember.entitlements
            .filter((entitlement: EntitlementData) => entitlement.isAvailableForOrderType(this.orderingContextModel.orderType))
            .filter((entitlement: EntitlementData) => !this.suppressEntitlement(entitlement));
        this.promotedDeals = this.allAvailableDeals
            .filter((entitlement: EntitlementData) => !entitlement.isTicket && entitlement.featureOnAppMenu)
            .slice(0, OrderingPageComponent.MAX_PROMOTED_DEALS);

        this.allUntriggeredCardPromos = this.identityModel.currentMember.promos
            .filter((promo: AppPromoData) =>
                promo.isAvailableForOrderType(this.orderingContextModel.orderType) &&
                !promo.triggered &&
                promo.showAppPromoCard &&
                (!promo.promotedSellables.length || this.promoModel.canPromoteSellablesAsCard(promo, true)));
        this.promotedCardPromos = this.allUntriggeredCardPromos
            .filter((promo: AppPromoData) => promo.featureOnAppMenu)
            .slice(0, OrderingPageComponent.MAX_PROMOTED_CARD_PROMOS);
        this.triggeredAndUnsuppressedCardPromos = (!this.orderModel.hasOrderedItems() ? []
            : (this.identityModel.currentMember.promos.filter((promo: AppPromoData) =>
                promo.isAvailableForOrderType(this.orderingContextModel.orderType) &&
                this.cardPromoIsTriggered(promo))))
            .concat(this.promotedCardPromos.filter((promo: AppPromoData) => !this.suppressCardPromo(promo)))
            .slice(0, OrderingPageComponent.MAX_PROMOTED_CARD_PROMOS);

        this.morePromotionalCards = this.allAvailableDeals.length > OrderingPageComponent.MAX_PROMOTED_DEALS || this.allUntriggeredCardPromos.length > OrderingPageComponent.MAX_PROMOTED_CARD_PROMOS;

        const testMultipleCards: boolean = false;
        if (testMultipleCards) {
            this.promotedDeals = this.promotedDeals.reduce((res, current) => res.concat(Array(9).fill(current)), []);
            this.promotedCardPromos = this.promotedCardPromos.reduce((res, current) => res.concat(Array(9).fill(current)), []);
            this.morePromotionalCards = true;
        }

        const testZeroCards: boolean = false;
        if (testZeroCards) {
            this.promotedDeals = [];
            this.allUntriggeredCardPromos = [];
            this.promotedCardPromos = [];
        }

        this.changeDetectorRef.detectChanges();
    }

    private suppressEntitlement(entitlement: EntitlementData): boolean {
        return entitlement.suppressDealCardIfAnyOrdered ? this.orderModel.personalOrderStarted
            : entitlement.suppressDealCardIfOrdered ? this.dealModel.hasOrderedIncludedSellables(entitlement)
                : false;
    }

    private cardPromoIsTriggered(promo: AppPromoData): boolean {
        return promo.triggered && promo.showAppPromoCard &&
            this.promoModel.hasOrderedTriggerSellable(promo) &&
            (!promo.promotedSellables.length || this.promoModel.canPromoteSellablesAsCard(promo, true));
    }

    private suppressCardPromo(promo: AppPromoData): boolean {
        return promo.suppressPromoCardIfAnyOrdered ? this.orderModel.personalOrderStarted
            : promo.suppressPromoCardIfOrdered ? this.promoModel.hasOrderedPromotedSellables(promo)
                : false;
    }

    /* ================
        PROMO HANDLING
       ================ */

    public async promoActioned(promo: AppPromoData, promoAction: AppPromoAction, menuItem: MenuItemData, orderSimpleProductsDirectly: boolean = false, ignoreSizes: boolean = false): Promise<void> {
        if (promoAction === AppPromoAction.ORDER_PRODUCT) {
            return this.orderItem(new MenuItemSelection(menuItem), orderSimpleProductsDirectly, ignoreSizes);
        }
        if (promoAction === AppPromoAction.SHOW_PRODUCTS) {
            await this.orderingContextController.untilExpectedCompletionEvaluationComplete();
            this.promoModel.setActivatedPromo(promo);
        }
    }

    /**
     * Presents any necessary triggered popup promo.
     * @return true if a promo is actioned, false otherwise.
     */
    private async presentTriggeredPopupPromo(): Promise<boolean> {
        if (!this.reordering && SessionEvents.orderingPhase$.value === OrderingPhase.ORDERING && this.identityModel.currentMember) {
            const promoAction: AppPromoAction = await this.presentPopupPromo(this.promoModel.selectTriggeredPopupWhenOrderedPromo(this.identityModel.currentMember.promos));

            // Go no further if the popup promo is actioned - order will be re-initiated if a new product is ordered, or if the user requested to show promoted products, any progress action is cancelled.
            if (promoAction === AppPromoAction.ORDER_PRODUCT) {
                return true;
            }
            if (promoAction === AppPromoAction.SHOW_PRODUCTS) {
                this.pendingCheckoutAction = undefined;
                this.changeDetectorRef.detectChanges();
                return true;
            }
        }
        return false;
    }

    /**
     * Presents any necessary checkout popup promo.
     * If an order-product action is accepted, the post-initiation action is set to resume checkout after the product is ordered and the order is re-initiated.
     * @return true if a promo is actioned, requiring checkout to be stopped or deferred, false otherwise.
     */
    private async presentCheckoutPopupPromo(): Promise<boolean> {
        if (this.identityModel.currentMember) {
            const promoAction: AppPromoAction = await this.presentPopupPromo(this.promoModel.selectCheckoutPopupPromo());
            if (promoAction === AppPromoAction.ORDER_PRODUCT) {
                this.pendingCheckoutAction = null;
                return true;
            }
            if (promoAction === AppPromoAction.SHOW_PRODUCTS) {
                return true;
            }
        }
        return false;
    }

    /**
     * Pops up the given promo and awaits the result.
     * Returns a promise of a boolean indicating whether a product has been ordered from the popup.
     * @param selection
     * @return the promo action, or undefined if the promo is not actioned
     */
    private async presentPopupPromo(selection: {
        promo: AppPromoData,
        menuItem?: MenuItemData
    }): Promise<AppPromoAction> {
        if (!selection || this.sessionModel.waiterMode) {
            return undefined;
        }

        this.loadingService.dismissSpinner();

        const actioned: boolean = await this.navigationService.createPage("woa-promo-page", selection);
        if (selection.menuItem && actioned) {
            // noinspection ES6MissingAwait
            this.promoActioned(selection.promo, selection.promo.popupAction, selection.menuItem, true, true);
            return selection.promo.popupAction;
        }
        return undefined;
    }

    /* ===================
        MENU PRESENTATION
       =================== */

    public viewingMenuItems$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    @ViewChild("anchorPanel", {read: ElementRef}) public anchorPanelER: ElementRef;
    @ViewChild("desktopHomePage") private desktopHomePage: DesktopHomePageLayoutComponent;

    public get showMenu(): boolean {
        return this.isLoaded && !!MenuEvents.currentMenu.value;
    }

    public async viewCategory(): Promise<void> {
        if (this.responsiveService.mobile && this.pageLayout && this.pageLayout instanceof FullPageLayoutComponent) {
            this.pageLayout.pushScrollPosition();
            await this.pageLayout.fadeOutAndIn();
            this.viewingMenuItems$.next(true);
            await this.navigationService.createView("Category View");
            if (this.viewingMenuItems$.value) {
                if (this.responsiveService.mobile && this.pageLayout && this.pageLayout instanceof FullPageLayoutComponent) {
                    await this.pageLayout.fadeOutAndIn();
                    this.viewingMenuItems$.next(false);
                    this.pageLayout.popScrollPosition();
                    if (this.menuModel.selectedCategory && !this.menuModel.selectedCategory.id) {
                        this.menuModel.deactivatePromotedCategory();
                    }
                } else {
                    this.viewingMenuItems$.next(false);
                }
            }
        }
    }

    public get waitTimeNotice(): string {
        if (!this.orderingContextModel.showWaitTimeNotices || (this.responsiveService.mobile && this.viewingOrder$.value)) {
            return undefined;
        }
        if (this.responsiveService.mobile && !this.viewingMenuItems$.value) {
            return this.orderingContextModel.showVenueWaitTimeNotice ? "woa.message.order.venueWaitTimeNotice" : undefined;
        }

        const venueWaitTime: number = this.orderingContextModel.venueWaitTime;
        const categoryWaitTime: number = this.menuModel.selectedCategoryWaitTime;
        return this.orderingContextModel.showVenueWaitTimeNotice && venueWaitTime > categoryWaitTime ? "woa.message.order.categoryVenueWaitTimeNotice"
            : categoryWaitTime ? "woa.message.order.categoryWaitTimeNotice"
                : undefined;
    }

    public refreshScrolling(): void {
        setTimeout(() => {
            if (this.responsiveService.desktop && this.desktopHomePage) {
                this.desktopHomePage.positionSidebar();
            }
        });
    }

    public async searchMenu(): Promise<void> {
        return this.navigationService.createPage("woa-menu-search-page", undefined, {alignment: PageAlignment.TOP}, false, true);
    }

    /* ==================
        ORDER MANAGEMENT
       ================== */

    private static readonly SHOW_ORDER_SUMMARY_ICON: boolean = false;

    public get showOrderSummaryIcon(): boolean {
        return OrderingPageComponent.SHOW_ORDER_SUMMARY_ICON && this.isLoaded && (this.orderModel.hasOrderedItems() || !!this.dealModel.currentEntitlement);
    }

    /**
     *
     * @param menuItemSelection
     * @param orderSimpleProductsDirectly Bypass the item ordering page if the product is simple
     * @param ignoreSizes Do not consider other items in the product size group when checking for a simple product
     * @param fromMenu If not ordering from menu, out of stock menu items are included, and the specified menu item is not prioritised in the search
     */
    public async orderItem(menuItemSelection: MenuItemSelection,
                           orderSimpleProductsDirectly: boolean = false,
                           ignoreSizes: boolean = false,
                           fromMenu: boolean = true): Promise<void> {

        let itemOrdered: boolean = false;
        try {

            // If the menu is reloaded due to store selection after initially selection the item, it may become unavailable (store filtering is done on the server).
            if (!this.menuModel.menuContainsItem(menuItemSelection.menuItem)) {
                await this.notificationService.displayAlert(null, "woa.message.order.productNotAvailableAtStore", {item: menuItemSelection.menuItem.name});
                return;
            }

            // Because a sellable can be added to the menu under multiple menu items, and different details may be specified for each,
            // we want to make sure the menu item used to determine how the product is ordered and ultimately placed in the order for schedule validation
            // reflects a consistent set of details such as available schedules, out-of-stock, product image, and size group regardless of which item the user selected.
            const mergedMenuItem: MenuItemData = this.menuModel.findMergedItemForSellable(menuItemSelection.menuItem.sellable.sellableId, this.orderingContextModel.orderType,
                undefined, fromMenu ? menuItemSelection.menuItem : undefined, !fromMenu);

            if (!this.menuModel.itemIsAvailableForOrderType(menuItemSelection.menuItem)) {
                await this.notificationService.displayAlert(null,
                    !(this.orderingContextModel.singleStoreLocation && this.orderingContextModel.orderType) ? "woa.message.order.productNotAvailableAtStore"
                        : this.orderingContextModel.orderType === OrderType.ORDER_AHEAD ? "woa.message.order.productNotAvailableOrderAhead"
                            : this.orderingContextModel.orderType === OrderType.ORDER_DELIVERY ? "woa.message.order.productNotAvailableOrderDelivery"
                                : "woa.message.order.productNotAvailableOrderAtTable", {item: menuItemSelection.menuItem.name});
                return;
            }
            if (this.menuModel.itemOutOfStock(mergedMenuItem)) {
                await this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: menuItemSelection.menuItem.name});
                return;
            }
            if (this.orderModel.menuItemAtLimit(mergedMenuItem)) {
                await this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: menuItemSelection.menuItem.name});
                return;
            }
            await this.orderingContextController.untilExpectedCompletionEvaluationComplete();
            if (!this.menuModel.itemIsAvailableOnSchedules(mergedMenuItem)) {
                await this.notificationService.displayAlert(null, "woa.message.order.productNotAvailableAtSchedules", {item: menuItemSelection.menuItem.name});
                return;
            }

            if (!this.orderingContextModel.currentStore || !this.orderingContextModel.orderType) {
                return this.orderingContextController.selectAndApplyStoreAndOrderType(new OrderingContext()
                    .withIntention(OrderingContextIntention.ORDER)
                    .withSelectedMenuItem(new MenuItemSelection(mergedMenuItem, menuItemSelection.category))
                    .withSelectedTable(this.orderingContextModel.preselectedTableSelection));
            }

            if (this.orderingContextModel.currentStore.minimumAge && mergedMenuItem.sellable.applyMinimumAge && !await this.identityModel.verifyAge()) {
                return;
            }

            if (orderSimpleProductsDirectly && this.menuModel.isSimpleProductItem(mergedMenuItem, ignoreSizes)) {
                const orderItem: OrderItem = this.orderModel.createOrderItem(mergedMenuItem, (mergedMenuItem.sellable.minWebOrderQty || 1));
                this.orderModel.addOrderItem(orderItem);
                // noinspection ES6MissingAwait
                this.analyticsService.trackItemAddedToOrder(orderItem, mergedMenuItem.promotedFeatureTag);
                itemOrdered = true;
                return;
            }

            // noinspection ES6MissingAwait
            this.analyticsService.trackViewItem(menuItemSelection.menuItem);
            const orderItem: OrderItem = await this.navigationService.createPage("woa-order-item-edit-page", {
                menuItem: mergedMenuItem,
                category: menuItemSelection.category
            });
            itemOrdered = !!orderItem;
        } finally {
            if (!itemOrdered) {
                // Trigger a check for any triggered promos due to items previously added
                setTimeout(() => this.orderModel.notifyOrderInitiationStatus());
            }
        }
    }

    public async orderDeal(entitlement: EntitlementData): Promise<void> {
        await this.orderingContextController.untilExpectedCompletionEvaluationComplete();
        const result: EntitlementResult = await this.dealModel.orderEntitlement(entitlement);
        if (result.resultCode === EntitlementResultCode.UNAVAILABLE) {
            await this.notificationService.displayAlert(null, result.msg);
        }
    }

    private async orderPreselectedDealCode(): Promise<boolean> {
        const dealCode: string = this.orderingContextModel.getAndClearPreselectedDealCode();
        if (!dealCode) {
            return false;
        }
        const result: EntitlementResult = await this.dealModel.orderDealByDealCode(dealCode);
        if (result.resultCode === EntitlementResultCode.UNAVAILABLE) {
            await this.notificationService.displayAlert(null, "woa.message.order.dealNotValid");
        }
        return result.resultCode === EntitlementResultCode.IMMEDIATE;
    }

    private async orderDealProducts(status: EntitlementStatus): Promise<any> {
        if (SessionEvents.orderingPhase$.value !== OrderingPhase.ORDERING || status === EntitlementStatus.ORDERING_PRODUCTS) {
            return;
        }

        // Defer if still loading
        if (this.loadingStatus === LoadingStatus.RELOADING && status === EntitlementStatus.PRODUCT_ORDER_REQUIRED) {
            return setTimeout(() => this.dealModel.notifyCurrentEntitlementStatus(), 500);
        }

        if (this.loadingStatus !== LoadingStatus.LOADED) {
            return;
        }

        this.changeDetectorRef.detectChanges();
        if (status !== EntitlementStatus.PRODUCT_ORDER_REQUIRED) {
            this.orderModel.requestOrderInitiation("ordering page - order deal products");
            return;
        }

        if (this.dealModel.shouldApplyMinimumAgeToEntitlement(this.dealModel.currentEntitlement) && !await this.identityModel.verifyAge()) {
            return;
        }

        let unavailableSchedules: boolean = false;
        let unavailableQuantity: boolean = false;
        let itemsToOrder: OrderItem[] = [];
        let accessibleOutlets: number[] = this.dealModel.currentEntitlement.discount.accessibleOutlets || this.dealModel.currentEntitlement.mixAndMatch.accessibleOutlets;
        if ((accessibleOutlets && accessibleOutlets.indexOf(this.orderingContextModel.currentStore.outletId) < 0) ||
            !this.dealModel.currentEntitlement.products.every((entitlementProduct: EntitlementProductData) => {
                const menuItem: MenuItemData = this.menuModel.findMergedItemForSellable(entitlementProduct.sellable.sellableId, this.orderingContextModel.orderType, undefined, undefined, true);
                if (!menuItem) {
                    return false;
                }
                if (!this.menuModel.itemIsAvailableOnSchedules(menuItem)) {
                    unavailableSchedules = true;
                    return false;
                }
                if (this.menuModel.itemOutOfStock(menuItem)) {
                    unavailableQuantity = true;
                    return false;
                }
                if (this.orderModel.menuItemAtLimit(menuItem, entitlementProduct.quantity || 1)) {
                    unavailableQuantity = true;
                    return false;
                }
                for (let index = 0; index < (entitlementProduct.quantity || 1); index++) {
                    let orderItem: OrderItem = this.orderModel.createOrderItem(menuItem, menuItem.sellable.minWebOrderQty || 1);
                    orderItem.deal = true;
                    itemsToOrder.push(orderItem);
                }
                return true;
            })
        ) {
            this.dealModel.removeCurrentEntitlement();
            if (unavailableSchedules) {
                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null, "woa.message.order.dealNotAvailableAtSchedules");
            } else if (unavailableQuantity) {
                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null, "woa.message.order.dealOutOfStock");
            } else if (this.orderingContextModel.singleStoreLocation && this.orderingContextModel.orderType) {
                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null,
                    this.orderingContextModel.orderType === OrderType.ORDER_AHEAD ? "woa.message.order.dealNotAvailableOrderAhead"
                        : this.orderingContextModel.orderType === OrderType.ORDER_DELIVERY ? "woa.message.order.dealNotAvailableOrderDelivery"
                            : "woa.message.order.dealNotAvailableOrderAtTable");
            } else {
                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null, "woa.message.order.dealNotAvailableAtStore");
            }
            return;
        } else if (itemsToOrder.length > 0) {
            this.dealModel.currentEntitlementStatus = EntitlementStatus.ORDERING_PRODUCTS;
            // noinspection ES6MissingAwait
            this.navigationService.createPage("woa-order-item-edit-page", {itemsToOrder: itemsToOrder});
        }

        this.changeDetectorRef.detectChanges();
    }

    private async orderInitiationStatusChanged(status: OrderInitiationStatus): Promise<void> {
        if (status === OrderInitiationStatus.INITIATED) {
            if (OrchestrationEvents.initialLoadStatus$.value === LoadingStatus.LOADED) {
                await this.checkOnLoadPrompts("Order initiated", this.orderModel.canReorderOnLoad);
            }
            if (await this.presentTriggeredPopupPromo()) {
                return;
            }
            this.reinitiateDeferredCheckout();
        } else if (status === OrderInitiationStatus.FAILED) {
            this.pendingCheckoutAction = undefined;
        }
        if (!this.reordering && SessionEvents.orderingPhase$.value === OrderingPhase.ORDERING) {
            this.filterPromotionalCards();
        }
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Attempts to restore the locally persisted current order.
     * Returns true if the order exists and has order items.
     */
    private async restoreOrder(): Promise<boolean> {
        if (!(MenuEvents.currentMenu.value && this.orderingContextModel.currentStore)) {
            return false;
        }
        const recentOrder: RecentOrderData = this.orderModel.restoreOrder();
        if (!recentOrder) {
            return false;
        }

        if (!this.orderingContextModel.orderType) {
            await this.orderingContextModel.applyOrderType(recentOrder.orderInitiation.orderTypeEnum);
            this.orderModel.requestOrderInitiation("restore order", true);
        }

        // Attempt to Recreate or update the group order that was current when the order was initiated
        if (recentOrder.orderInitiation.orderTransferTargetId && !this.groupOrderModel.hasGroupOrder && this.groupOrderModel.canHaveGroupOrder) {
            await this.groupOrderModel.saveGroupOrder(true, recentOrder.orderInitiation.transferTargetName, recentOrder.orderInitiation.orderTransferTargetId);
        }

        // Apply the entitlement that was attached to the order
        if (recentOrder.orderInitiation.entitlementId && recentOrder.entitlement) {
            this.dealModel.applyEntitlement(recentOrder.entitlement, EntitlementResultCode.IMMEDIATE);
        }

        return this.orderModel.hasOrderedItems();
    }

    /* ====================
        REORDER MANAGEMENT
       ==================== */

    private static readonly REORDER_CHECK_TIMEOUT: number = 60000 * 5;

    private reordering: boolean = false;
    private reorderTimerSubscription: Subscription;

    private checkReorder(): void {
        // If an order has been placed, calculate the amount of time before showing the reorder dialog
        if (this.orderModel.orderPlacementFinished) {
            let delay: number = OrderingPageComponent.REORDER_CHECK_TIMEOUT - (Date.now() - this.orderModel.orderPlacementFinished);
            if (delay < 0) {
                delay = 0;
            }
            this.startReorderTimer(delay);
            this.orderModel.orderPlacementFinished = undefined;
        }
    }

    /**
     * Prompts for reorder a period of time after the page has loaded after completion of an order.
     * If not in a state to prompt, the canReorderOnLoad state is reset to prompt the next time the page is loaded.
     */
    private startReorderTimer(initialDelay: number): void {
        if (this.reorderTimerSubscription) {
            this.stopReorderTimer();
        }
        this.reorderTimerSubscription = Observable.timer(initialDelay).subscribe(async () => {
            if (SessionEvents.orderingPhase$.value === OrderingPhase.ORDERING && this.orderModel.canPromptForReorder) {
                return this.openReorderPage();
            }
            this.orderModel.canReorderOnLoad = true;
        });
    }

    private stopReorderTimer(): void {
        if (this.reorderTimerSubscription) {
            this.reorderTimerSubscription.unsubscribe();
            this.reorderTimerSubscription = undefined;
        }
    }

    private async openReorderPage(): Promise<void> {
        this.reordering = true;
        const status: ReorderStatus = await this.navigationService.createPage("woa-reorder-page");
        if (!(status && status.quickPay)) {
            this.reordering = false;

            // Handle a group order reinitiation error by opening the group order page to display the error and allow retry
            if (status && (status.groupOrderValidationMessages || status.groupOrderError)) {
                await this.navigationService.createPage("woa-group-order-page", {
                    mode: GroupOrderPageMode.EDIT,
                    reorderStatus: status
                });
            }

            // Trigger a check for any triggered promos due to items added in the reorder
            this.orderModel.notifyOrderInitiationStatus();
        }
    }

    /* ==========
        CHECKOUT
       ========== */

    public viewingOrder$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * How to progress with checkout after order initiation:
     *   undefined = no checkout;
     *   null = proceed to view-order or payment page
     *   otherwise = quick pay with specified payment type
     */
    private pendingCheckoutAction: PaymentType;

    public get checkoutPending(): boolean {
        return this.pendingCheckoutAction !== undefined;
    }

    public get otherPayLabel(): string {
        return this.checkoutPending ? "woa.message.order.initiating"
            : this.orderingContextModel.fulfilmentDetailsRequired ? "woa.button.checkout.checkout"
                : this.orderModel.orderTotal <= 0 ? "woa.button.checkout.placeOrder"
                    : this.orderModel.showQuickPay(false) && !this.suppressQuickPay ? "woa.button.checkout.payOther"
                        : "woa.button.checkout.payNow";
    }

    public get suppressQuickPay(): boolean {
        return this.responsiveService.mobile ? false : (this.promoModel.availableCheckoutPopupPromos && !!this.promoModel.availableCheckoutPopupPromos.length);
    }

    public get otherPayProminence(): ButtonProminence {
        return this.orderModel.showQuickPay(false) ? ButtonProminence.LOW : ButtonProminence.HIGH;
    }

    /**
     * For mobile - switches to order view mode.
     * Checkout promos are presented if required.
     * @param deferred
     */
    public async viewOrder(deferred: boolean = false): Promise<void> {
        this.promoModel.notifyCheckoutStarted(deferred);
        if (await this.presentCheckoutPopupPromo()) {
            return;
        }

        await this.sessionModel.checkLiveMode();

        if (this.pageLayout && this.pageLayout instanceof FullPageLayoutComponent) {
            this.pageLayout.pushScrollPosition();
            await this.pageLayout.fadeOutAndIn();
            this.viewingOrder$.next(true);
            this.changeDetectorRef.detectChanges();
            await this.navigationService.createView("Order View");
            if (this.viewingOrder$.value) {
                if (this.responsiveService.mobile && this.pageLayout && this.pageLayout instanceof FullPageLayoutComponent) {
                    await this.pageLayout.fadeOutAndIn();
                    this.viewingOrder$.next(false);
                    this.changeDetectorRef.detectChanges();
                    this.pageLayout.popScrollPosition();
                } else {
                    this.viewingOrder$.next(false);
                }
            }
        }
    }

    /**
     * Progresses to the payment page.
     * For desktop, checkout promos are presented if required.
     * @param deferred
     */
    public async checkout(deferred: boolean = false): Promise<void> {
        if (this.responsiveService.desktop) {
            this.promoModel.notifyCheckoutStarted(deferred);
        }

        if (!await this.isOrderValidForPayment()) {
            return;
        }

        if (this.orderModel.getQuickPayType() === PaymentType.NONE) {
            return this.quickPay(deferred, PaymentType.NONE);
        }

        // If group orders are in play, we want to force initiation of the order if it has not been initiated within the last 10 seconds, in order to pick up the current group orders.
        if (this.orderModel.initiationStatus !== OrderInitiationStatus.INITIATED ||
            (this.groupOrderModel.canHaveGroupOrder && new Date().getTime() - this.orderModel.orderInitiatedTime > 10000)) {
            this.pendingCheckoutAction = null;
            this.orderModel.requestOrderInitiation("ordering page - check group orders");
            return;
        }

        if (await this.loginForCheckout(null)) {
            return;
        }

        if (this.responsiveService.desktop && await this.presentCheckoutPopupPromo()) {
            return;
        }

        this.pendingCheckoutAction = undefined;

        // For tyro, we can't prompt for tips on the payment page
        if (this.paymentModel.paymentProvider === IntegratedPaymentProvider.TYRO && !await this.orderingContextModel.promptForTip()) {
            return;
        }

        return this.navigationService.createPage("woa-payment-page");
    }

    public async quickPay(deferred: boolean = false, paymentType: PaymentType): Promise<any> {
        if (!await this.isOrderValidForPayment(paymentType)) {
            return;
        }

        if (await this.loginForCheckout(paymentType)) {
            return;
        }

        this.pendingCheckoutAction = undefined;
        return this.orderModel.placeOrder(paymentType, this.groupOrderModel.preferredTransferTarget);
    }

    /**
     * Validates that the order is ready for checkout with the (optionally) specified payment type.
     * If the order is valid, but order initiation is still pending, the post-initiation action is set to resume checkout after initiation completes.
     * @return true if the checkout can proceed, or false if it should be stopped or deferred.
     */
    private async isOrderValidForPayment(paymentType?: PaymentType): Promise<boolean> {
        if (this.checkoutPending || this.notificationService.alertDisplayed$.value) {
            return false;
        }
        this.pendingCheckoutAction = paymentType;
        this.changeDetectorRef.detectChanges();

        await this.orderingContextController.untilExpectedCompletionEvaluationComplete();
        if (!this.orderModel.validateOrderForPayment(paymentType, true)) {
            await this.analyticsService.trackNotReadyToPlaceOrder();
            this.pendingCheckoutAction = undefined;
            this.changeDetectorRef.detectChanges();
            return false;
        }

        if (this.orderingContextModel.fulfilmentDetailsRequired) {
            this.pendingCheckoutAction = undefined;
            // noinspection ES6MissingAwait
            this.orderingContextController.changeFulfilmentDetails();
            return false;
        }

        if (OrderEvents.orderInitiationStatus$.value === OrderInitiationStatus.INITIATING ||
            OrderEvents.orderSubmissionStatus$.value === OrderSubmissionStatus.SUBMITTING) {
            return false;
        }
        this.pendingCheckoutAction = undefined;
        return true;
    }

    /**
     * Performs a login if guest ordering is not valid for the specified payment type.
     * If a login is completed, the post-initiation action is set to resume checkout after the order is re-initiated.
     * @return true if login is required, requiring checkout to be stopped or deferred, false otherwise.
     */
    private async loginForCheckout(paymentType?: PaymentType): Promise<boolean> {
        if (this.identityModel.isGuestUser && !this.sessionModel.loginOptions.allowGuestOrdering && paymentType !== PaymentType.GROUP_PAY) {
            if (await this.navigationService.createPage("woa-identification-page", {"mode": IdentificationMode.LOGIN})) {
                this.pendingCheckoutAction = paymentType;
                this.changeDetectorRef.detectChanges();
                this.requestReload("logged in on checkout", false, false, true, true);
                return true;
            }
            this.pendingCheckoutAction = undefined;
            this.changeDetectorRef.detectChanges();
            return true;
        }
        return false;
    }

    private reinitiateDeferredCheckout(): void {
        if (this.checkoutPending) {
            setTimeout(() => {
                const paymentType: PaymentType = this.pendingCheckoutAction;
                this.pendingCheckoutAction = undefined;

                if (paymentType !== null) {
                    return this.quickPay(true, paymentType);
                } else if (this.responsiveService.mobile && !this.viewingOrder$.value) {
                    return this.viewOrder(true);
                } else {
                    return this.checkout(true);
                }
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/ordering-page/ordering-page.component.ts